import { Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePicker } from '../ui/date-picker';
import { format } from 'date-fns';

interface FilterOption {
  label: string;
  value: string;
}

type FilterPreset = 'custom' | 'monthly' | 'semester' | 'quarterly' | 'period';

interface FilterWidgetProps {
  title?: string;
  field?: string;
  preset?: FilterPreset;
  options?: FilterOption[];
  activeFilters?: Record<string, string>;
  onChange?: (field: string, value: string) => void;
  allowAll?: boolean;
  isCompact?: boolean;
}

const PRESET_FILTERS = {
  monthly: {
    field: 'mes',
    title: 'Filtro por Mês',
    options: [
      { label: 'Janeiro', value: '01' },
      { label: 'Fevereiro', value: '02' },
      { label: 'Março', value: '03' },
      { label: 'Abril', value: '04' },
      { label: 'Maio', value: '05' },
      { label: 'Junho', value: '06' },
      { label: 'Julho', value: '07' },
      { label: 'Agosto', value: '08' },
      { label: 'Setembro', value: '09' },
      { label: 'Outubro', value: '10' },
      { label: 'Novembro', value: '11' },
      { label: 'Dezembro', value: '12' },
    ],
  },
  semester: {
    field: 'semestre',
    title: 'Filtro por Semestre',
    options: [
      { label: '1º Semestre', value: '1' },
      { label: '2º Semestre', value: '2' },
    ],
  },
  quarterly: {
    field: 'trimestre',
    title: 'Filtro por Trimestre',
    options: [
      { label: '1º Trimestre', value: '1' },
      { label: '2º Trimestre', value: '2' },
      { label: '3º Trimestre', value: '3' },
      { label: '4º Trimestre', value: '4' },
    ],
  },
  period: {
    field: 'periodo',
    title: 'Filtro por Período',
    options: [], // Not used for period
  },
};

export default function FilterWidget({
  title,
  field,
  preset = 'custom',
  options = [],
  activeFilters = {},
  onChange,
  allowAll = true,
  isCompact = false,
}: FilterWidgetProps) {
  // Usar preset se definido, senão usar props customizados
  const filterConfig = preset !== 'custom' && PRESET_FILTERS[preset]
    ? PRESET_FILTERS[preset]
    : { 
        field: field || 'campo', 
        title: title || 'Filtro', 
        options: Array.isArray(options) && options.length > 0 
          ? options.map(opt => typeof opt === 'string' ? { label: opt, value: opt } : opt)
          : []
      };

  // Determinar o valor atual do filtro baseado no campo
  const currentValue = activeFilters[filterConfig.field] || '';

  // Para filtro de período, parsear as datas
  let startDate: Date | undefined;
  let endDate: Date | undefined;
  if (preset === 'period' && currentValue) {
    try {
      const parsed = JSON.parse(currentValue);
      startDate = parsed.start ? new Date(parsed.start) : undefined;
      endDate = parsed.end ? new Date(parsed.end) : undefined;
    } catch (e) {
      // Ignore invalid JSON
    }
  }

  const handleChange = (newValue: string) => {
    if (onChange && filterConfig.field) {
      // Se for "all", limpar o filtro (passar string vazia)
      onChange(filterConfig.field, newValue === 'all' ? '' : newValue);
    }
  };

  const handlePeriodChange = (start: Date | undefined, end: Date | undefined) => {
    if (onChange && filterConfig.field) {
      const value = start || end ? JSON.stringify({
        start: start ? format(start, 'yyyy-MM-dd') : null,
        end: end ? format(end, 'yyyy-MM-dd') : null,
      }) : '';
      onChange(filterConfig.field, value);
    }
  };

  // Se for custom e não tiver opções, mostrar mensagem
  const hasOptions = filterConfig.options.length > 0;
  const isCustom = preset === 'custom';
  const isPeriod = preset === 'period';

  return (
    <div className={isCompact ? "flex flex-col" : "h-full flex flex-col"}>
      {isCompact ? (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{filterConfig.title}:</span>
          {isPeriod ? (
            <div className="flex gap-2">
              <DatePicker
                date={startDate}
                onDateChange={(date) => handlePeriodChange(date, endDate)}
                placeholder="Data inicial"
                className="w-auto"
              />
              <span className="flex gap-2 items-center  text-sm">até</span>
              <DatePicker
                date={endDate}
                onDateChange={(date) => handlePeriodChange(startDate, date)}
                placeholder="Data final"
                className="w-auto"
              />
            </div>
          ) : isCustom && !hasOptions ? (
            <span className="text-xs text-muted-foreground">
              Configure as opções
            </span>
          ) : (
            <Select value={currentValue} onValueChange={handleChange}>
              <SelectTrigger className="w-auto min-w-[120px] h-8">
                <SelectValue placeholder={allowAll ? "Todos" : "Selecione..."} />
              </SelectTrigger>
              <SelectContent>
                {allowAll && <SelectItem value="all">Todos</SelectItem>}
                {filterConfig.options.filter((option) => option.value !== '').map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : (
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4" />
              {filterConfig.title}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {isPeriod ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Data Inicial</label>
                  <DatePicker
                    date={startDate}
                    onDateChange={(date) => handlePeriodChange(date, endDate)}
                    placeholder="Selecione a data inicial"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Data Final</label>
                  <DatePicker
                    date={endDate}
                    onDateChange={(date) => handlePeriodChange(startDate, date)}
                    placeholder="Selecione a data final"
                  />
                </div>
              </div>
            ) : isCustom && !hasOptions ? (
              <p className="text-sm text-muted-foreground">
                Configure as opções do filtro nas configurações do componente.
              </p>
            ) : (
              <Select value={currentValue} onValueChange={handleChange}>
                <SelectTrigger>
                  <SelectValue placeholder={allowAll ? "Todos" : "Selecione..."} />
                </SelectTrigger>
                <SelectContent>
                  {allowAll && <SelectItem value="all">Todos</SelectItem>}
                  {filterConfig.options.filter((option) => option.value !== '').map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
